/**
 * Tabs Overflow Menu
 *
 * Dropdown menu showing hidden tabs when there are too many to display.
 */

import { useTabsContext } from '@/context/TabsContext';
import type { TabItem } from '@/context/TabsContext/types';
import {
  getTabDisplayTitle,
  getTabRouteGroupKey,
  getTabRouteGroupLabel,
} from '@/utils/routeUtils';
import { DownOutlined, PushpinFilled } from '@ant-design/icons';
import { Badge, Button, Dropdown, type MenuProps, Space, Tooltip, Typography } from 'antd';
import React, { useMemo } from 'react';

const { Text } = Typography;

type TabsOverflowMenuProps = {
  hiddenTabs: TabItem[];
  paneId?: string;
  activeTabKey?: string | null;
  /** Group tabs by route prefix when 2+ tabs share the same section (default: true) */
  enableGrouping?: boolean;
};

type OverflowMenuEntry =
  | { type: 'single'; tab: TabItem }
  | { type: 'group'; groupKey: string; tabs: TabItem[] };

function getTabCurrentPath(tab: TabItem): string {
  return tab.history[tab.historyIndex]?.path || tab.path;
}

export function buildOverflowMenuEntries(
  hiddenTabs: TabItem[],
  enableGrouping: boolean,
): OverflowMenuEntry[] {
  if (!enableGrouping || hiddenTabs.length === 0) {
    return hiddenTabs.map((tab) => ({ type: 'single', tab }));
  }

  const tabsByGroup = new Map<string, TabItem[]>();
  for (const tab of hiddenTabs) {
    const groupKey = getTabRouteGroupKey(getTabCurrentPath(tab));
    tabsByGroup.set(groupKey, [...(tabsByGroup.get(groupKey) || []), tab]);
  }

  const emittedGroups = new Set<string>();
  const entries: OverflowMenuEntry[] = [];

  for (const tab of hiddenTabs) {
    const groupKey = getTabRouteGroupKey(getTabCurrentPath(tab));
    if (emittedGroups.has(groupKey)) {
      continue;
    }
    emittedGroups.add(groupKey);

    const groupTabs = tabsByGroup.get(groupKey) || [tab];
    if (groupTabs.length >= 2) {
      entries.push({ type: 'group', groupKey, tabs: groupTabs });
    } else {
      entries.push({ type: 'single', tab: groupTabs[0] });
    }
  }

  return entries;
}

function OverflowTabLabel({
  tab,
  isActive,
}: {
  tab: TabItem;
  isActive: boolean;
}) {
  const displayTitle = getTabDisplayTitle(tab);

  return (
    <Tooltip title={displayTitle} mouseEnterDelay={0.5}>
      <Space>
        {tab.pinned && <PushpinFilled style={{ fontSize: 12, color: '#1890ff' }} />}
        <Text
          style={{
            fontWeight: isActive ? 600 : 400,
            maxWidth: 200,
          }}
          ellipsis
        >
          {displayTitle}
        </Text>
      </Space>
    </Tooltip>
  );
}

export const TabsOverflowMenu: React.FC<TabsOverflowMenuProps> = ({
  hiddenTabs,
  paneId,
  activeTabKey: paneActiveTabKey,
  enableGrouping = true,
}) => {
  const { focusTab, activeTabKey } = useTabsContext();
  const effectiveActiveTabKey = paneActiveTabKey ?? activeTabKey;

  const makeTabItem = (tab: TabItem): NonNullable<MenuProps['items']>[number] => ({
    key: tab.key,
    label: (
      <OverflowTabLabel tab={tab} isActive={tab.key === effectiveActiveTabKey} />
    ),
    onClick: () => focusTab(tab.key, paneId),
  });

  const items: MenuProps['items'] = useMemo(() => {
    const entries = buildOverflowMenuEntries(hiddenTabs, enableGrouping);

    return entries.flatMap((entry) => {
      if (entry.type === 'single') {
        return [makeTabItem(entry.tab)];
      }

      return [{
        type: 'group' as const,
        label: `${getTabRouteGroupLabel(entry.groupKey)} ×${entry.tabs.length}`,
        children: entry.tabs.map((tab) => makeTabItem(tab)),
      }];
    });
  }, [hiddenTabs, enableGrouping, effectiveActiveTabKey, focusTab, paneId]);

  if (hiddenTabs.length === 0) {
    return null;
  }

  return (
    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
      <Badge count={hiddenTabs.length} size="small" offset={[-2, 2]}>
        <Button type="text" size="small" icon={<DownOutlined />} aria-label="Show hidden tabs" />
      </Badge>
    </Dropdown>
  );
};

export default TabsOverflowMenu;
